import React, { useState, useMemo } from 'react';
import { notification } from 'tea-component';
import { updateGamePillar } from '@gp/services/projectDetail';
import classnames from 'classnames';
import GamePillarList from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/GamePillar/components/GamePillarList';
import GamePillarModal from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/GamePillar/components/GamePillarModal';
import Loading from '@gp/components/Loading';
import { reportBtnOperationLog } from '@gp/utils/common';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import './index.scss';
import CardSlot from '@/pages/globalpedia/components/CardSlot';
import HistoryBubble from '@gp/pages/Pipeline/ProjectDetails/components/HistoryBubble';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';
import { GAME_PILLAR_IMG_LIST } from '@gp/pages/Pipeline/ProjectDetails/GameDetail/constants';

const getDataStats = (item: any) => {
  if (
    item?.introduction?.trim() === '' &&
    item?.title?.trim() === '' &&
    item?.cloud_path?.trim() === ''
  ) {
    return true;
  }
  if (item?.title?.trim() === '') {
    return false;
  }
  return true;
};

export default function GamePillar(props: any) {
  const {
    resData,
    loading,
    gameInformationData,
    // 是否有编辑权限
    userIsAbleEdit,
    iconList,
    refreshFun,
  } = props;

  const headTitle = 'Game Pillar';

  const [visible, setVisible] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [imgList, setImgList] = useState(GAME_PILLAR_IMG_LIST);
  const [initialParams, setInitialParams] = useState(GAME_PILLAR_IMG_LIST);

  useDeepCompareEffect(() => {
    const gamePillarDatas = resData?.data?.pillar_list || [];
    if (Array.isArray(gamePillarDatas) && gamePillarDatas?.length > 0) {
      const mergedArray = [
        ...gamePillarDatas,
        ...GAME_PILLAR_IMG_LIST.slice(gamePillarDatas.length),
      ];
      const sortArr = mergedArray?.map((item: any, index: number) => {
        return {
          ...item,
          sort: (index + 1)?.toString(),
          status: getDataStats(item),
          loading: false,
        };
      });
      setImgList(sortArr);
      setInitialParams(sortArr);
    } else {
      setImgList(GAME_PILLAR_IMG_LIST);
      setInitialParams(GAME_PILLAR_IMG_LIST);
    }
  }, [resData]);

  const close = useMemoizedFn(() => {
    setVisible(false);
  });

  const handleConfirm = useMemoizedFn(async (value) => {
    try {
      const { project_id: projectId, studio_entity_id: studioId } = gameInformationData;

      const datasList = value?.filter((item: any) => item.title !== '') || [];
      const sortList = datasList?.map((item: any, index: number) => {
        return {
          ...item,
          cover: '',
          sort: (index + 1)?.toString(),
        };
      });
      const params = {
        project_id: projectId,
        studio_id: studioId,
        pillar_list: sortList,
      } as any;

      await updateGamePillar(params);
      refreshFun?.();
      notification.success({
        description: 'Save success',
        duration: 1000,
      });
      close();
      deleteSaveParamsByPageKey(pageKeyCur);
    } catch (error) {
      notification.error({
        description: 'Save failed. Please check the network',
        duration: 1000,
      });
    } finally {
      reportBtnOperationLog('DETAIL_PROJECT_DETAIL_GAME_INFORMATION_EDIT_SUBMIT');
    }
  });

  const handleAutoSaveConfirm = () => {
    const saveValue = getSaveParamsValue(pageKeyCur) || [];
    setImgList(saveValue);
    setAutoSaveVisible(false);
    setVisible(true);
  };

  const handleAutoSaveCancel = () => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(pageKeyCur);
    setImgList(initialParams);
    setVisible(true);
  };

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_gameDetails_game_pillar}_${gameInformationData?.project_id}`;
  }, [gameInformationData]);

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
  });

  const handleEditClick = useMemoizedFn(() => {
    // 如果有自动保存过的，先咨询
    if (showRestorePrompt(pageKeyCur)) {
      setAutoSaveVisible(true);
    } else {
      setVisible(true);
    }
  });

  return (
    <>
      <CardSlot
        className={classnames('dbg-project-detail__body-game-pillar', {
          'dbg-project-detail__body-game-pillar-datas': resData?.data?.pillar_list?.length,
        })}
        title={
          <div className="dbg-project-detail_horizontal-section-title dbg-project-overview-head-title">
            {headTitle}
            <HistoryBubble loading={loading} newTime={loading ? '-' : resData?.data?.update_time} />
          </div>
        }
        iconList={iconList}
        getChange={(value: any) => {
          if (value === 'pencil-m') {
            handleEditClick();
          }
        }}
      >
        {loading ? (
          <Loading loading style={{ height: 110, padding: 10 }} />
        ) : (
          <GamePillarList
            userIsAbleEdit={userIsAbleEdit}
            gamePillarDatas={resData?.data?.pillar_list || []}
            getAdd={() => {
              handleEditClick();
            }}
            headTitle={headTitle}
          />
        )}
      </CardSlot>
      {/* GamePillar 编辑弹窗 */}
      <GamePillarModal
        visible={visible}
        gameInformationData={gameInformationData}
        onConfirm={handleConfirm}
        pageKeyCur={pageKeyCur}
        imgList={imgList}
        setImgList={setImgList}
        initialParams={initialParams}
        gamePillarDatas={resData?.data?.pillar_list || []}
        onCancel={() => {
          deleteSaveParamsByPageKey(pageKeyCur);
          setImgList(initialParams);
          setVisible(!visible);
        }}
      />

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </>
  );
}
